import { useState, useCallback, useMemo } from 'react';
import { IS_COARSE_POINTER } from '../types/document.js';
import { useAuthFetch } from '../hooks/useAuthFetch.js';
import { useUpgradePrompt } from '../hooks/useUpgradePrompt.js';
import { UpgradeDialog } from './UpgradeDialog.js';
import { useBilling } from '../contexts/BillingContext.js';

export interface ExtractionPanelProps {
  pdfData: Uint8Array;
  fileName: string;
  onClose: () => void;
}

type OutputFormat = 'json' | 'csv' | 'text' | 'markdown';

const SERVER_URL = import.meta.env.VITE_SERVER_URL || '';

const DEFAULT_PROMPT =
  'Extract all information from this PDF document. Include every text element, table, ' +
  'number, date, name, address, and any other data present. Preserve the document structure. ' +
  'Output all extracted data in a well-organized format.';

const FORMAT_OPTIONS: { value: OutputFormat; label: string; ext: string }[] = [
  { value: 'markdown', label: 'Markdown', ext: '.md' },
  { value: 'json', label: 'JSON', ext: '.json' },
  { value: 'csv', label: 'CSV', ext: '.csv' },
  { value: 'text', label: 'Text', ext: '.txt' },
];

interface ProgressInfo {
  currentChunk: number;
  totalChunks: number;
  startPage: number;
  endPage: number;
  completed: number;
}

export function ExtractionPanel({ pdfData, fileName, onClose }: ExtractionPanelProps) {
  const { authFetch } = useAuthFetch();
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('markdown');
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState<ProgressInfo | null>(null);
  const upgrade = useUpgradePrompt();
  const { entitlement } = useBilling();

  const handleExtract = useCallback(async () => {
    // Client-side pre-check. The server gate is the real enforcement — this
    // just avoids base64-encoding and uploading a whole PDF only to have it
    // rejected, and gives an instant answer instead of a round trip.
    // `entitlement` may be null (still loading, or the lookup failed); in that
    // case fall through and let the server decide.
    if (entitlement && entitlement.billingEnabled && !entitlement.canExtract) {
      upgrade.show(entitlement.entitled ? 'quota_exhausted' : 'trial_expired');
      return;
    }

    setLoading(true);
    setResult('');
    setProgress(null);

    try {
      let binary = '';
      const chunkSize = 8192;
      for (let i = 0; i < pdfData.length; i += chunkSize) {
        binary += String.fromCharCode(...pdfData.subarray(i, i + chunkSize));
      }
      const pdfBase64 = btoa(binary);

      const response = await authFetch(`${SERVER_URL}/api/extract`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pdfBase64,
          prompt: DEFAULT_PROMPT,
          outputFormat,
          stream: true,
        }),
      });

      if (!response.ok) {
        // 402 = trial quota exhausted or subscription lapsed. Show the upgrade
        // prompt rather than a generic "Extraction failed", which would read
        // as a bug rather than a billing state.
        if (await upgrade.handleResponse(response)) {
          return; // `finally` clears the loading flag
        }
        throw new Error(`Extraction failed: ${response.statusText}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (reader) {
        let buffer = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const event = JSON.parse(line.substring(6)) as {
                  type: string;
                  text?: string;
                  error?: string;
                  progress?: {
                    currentChunk: number;
                    totalChunks: number;
                    startPage: number;
                    endPage: number;
                    stage: 'started' | 'completed';
                  };
                };
                if (event.type === 'text_delta' && event.text) {
                  setResult((prev) => prev + event.text);
                } else if (event.type === 'progress' && event.progress) {
                  const p = event.progress;
                  setProgress((prev) => ({
                    currentChunk: p.currentChunk,
                    totalChunks: p.totalChunks,
                    startPage: p.startPage,
                    endPage: p.endPage,
                    completed:
                      p.stage === 'completed'
                        ? Math.max((prev?.completed ?? 0) + 1, p.currentChunk)
                        : (prev?.completed ?? 0),
                  }));
                } else if (event.type === 'error' && event.error) {
                  setResult((prev) => prev + `\n\n[Error: ${event.error}]\n\n`);
                }
              } catch {
                // Skip malformed SSE lines
              }
            }
          }
        }
      }
    } catch (err) {
      setResult(`Error: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  }, [pdfData, authFetch, outputFormat, upgrade, entitlement]);

  const handleDownload = useCallback(() => {
    if (!result) return;
    const fmt = FORMAT_OPTIONS.find((f) => f.value === outputFormat);
    const ext = fmt?.ext ?? '.txt';
    const mimeTypes: Record<OutputFormat, string> = {
      markdown: 'text/markdown',
      json: 'application/json',
      csv: 'text/csv',
      text: 'text/plain',
    };
    // Strip the original file extension and append the format extension
    const baseName = fileName.replace(/\.[^.]+$/, '');
    const blob = new Blob([result], { type: mimeTypes[outputFormat] });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${baseName}${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  }, [result, outputFormat, fileName]);

  // Teams Store certification policy requires a clear way to report
  // objectionable AI-generated content, referencing the content in question.
  const reportMailto = useMemo(() => {
    const subject = `Report objectionable content: ${fileName || 'extraction'}`;
    const body = [
      'Please describe what was objectionable or inappropriate:',
      '',
      '',
      '---',
      `Document: ${fileName || '(unnamed)'}`,
      `Output format: ${outputFormat}`,
      '',
      'Extracted content in question:',
      (result || '').slice(0, 1500),
    ].join('\n');
    return `mailto:report@annoteer.app?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }, [fileName, outputFormat, result]);

  return (
    <div className="extraction-panel">
      <div className="extraction-panel__header">
        <h3>{loading ? 'Extracting...' : result ? 'Extracted Data' : 'Select Format'}</h3>
        <div className="extraction-panel__header-actions">
          {/* Hidden on touch: same platform refusal as the annotated export.
              Teams for Android answers a download with "cette action n'est
              actuellement pas prise en charge sur mobile", so the button can
              only produce an error that reads as our bug. The extracted text
              stays on screen and can be selected and copied. */}
          {result && !loading && !IS_COARSE_POINTER && (
            <button className="extraction-panel__download-btn" onClick={handleDownload}>
              Download
            </button>
          )}
          {result && !loading && (
            <a
              className="extraction-panel__report-link"
              href={reportMailto}
              target="_blank"
              rel="noreferrer"
              title="Report objectionable or inappropriate AI-generated content"
            >
              Report
            </a>
          )}
          <button onClick={onClose} className="extraction-panel__close">
            Close
          </button>
        </div>
      </div>
      {!loading && !result && (
        <>
          <span className="extraction-panel__format-label">Output format</span>
          <div className="extraction-panel__controls">
            {FORMAT_OPTIONS.map((fmt) => (
              <button
                key={fmt.value}
                className={`extraction-panel__format-btn ${outputFormat === fmt.value ? 'extraction-panel__format-btn--active' : ''}`}
                onClick={() => setOutputFormat(fmt.value)}
              >
                {fmt.label}
              </button>
            ))}
          </div>
          <button className="extraction-panel__extract-btn" onClick={handleExtract}>
            Extract
          </button>
        </>
      )}
      {loading && progress && (
        <div className="extraction-panel__progress" role="status" aria-live="polite">
          <div className="extraction-panel__progress-text">
            Processing chunk {progress.completed} of {progress.totalChunks}
            {progress.completed < progress.totalChunks && (
              <>
                {' '}
                &nbsp;·&nbsp; pages {progress.startPage}–{progress.endPage}
              </>
            )}
          </div>
          <div className="extraction-panel__progress-bar">
            <div
              className="extraction-panel__progress-bar-fill"
              style={{
                width: `${Math.min(100, Math.round((progress.completed / progress.totalChunks) * 100))}%`,
              }}
            />
          </div>
        </div>
      )}
      {loading && !result && !progress && (
        <p className="extraction-panel__loading">Analyzing PDF with AI...</p>
      )}
      {upgrade.reason && <UpgradeDialog reason={upgrade.reason} onClose={upgrade.clear} />}
      {result && <pre className="extraction-panel__result">{result}</pre>}
    </div>
  );
}
